import {
  checkpointDisplayLabel,
  type ScanRow,
} from "./scan-list";
import type { FieldPin } from "./field-pin-layers";
import type { ScanPoint } from "./map-points";

// Resolve a Live Activity row to a map location for CAD fly-to.
//
// Tier order (most honest first):
//  1. Exact join on scan_id → supervisor_scan_points.scan_point_id (post-host map apply)
//  2. Heuristic match on guard + nearest server time + checkpoint display title
//  3. Named checkpoint field pin (expected post) when the scan could not be mapped above
//
// Never invents coordinates. Free-form "Here (no pin)" without a scan GPS point returns null.

export type ActivityMapCoordSource =
  | "scan_gps"
  | "checkpoint_expected"
  | "checkpoint_pin"
  | "unknown";

export interface ActivityMapTarget {
  lat: number;
  lng: number;
  /** Operator caption, already redacted (checkpoint title · guard). */
  label: string;
  source: ActivityMapCoordSource;
  /** scan_point_id when the target came from the check-in layer; else null. */
  scanPointId: string | null;
  routeAssignmentId?: string | null;
  siteId?: string | null;
  occurredAt?: string | null;
}

const MATCH_WINDOW_MS = 120_000;

function parseIsoMs(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value.length === 0) {
    return null;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function titlesMatch(
  row: Pick<ScanRow, "checkpointName" | "presenceKind" | "scanMethod">,
  point: ScanPoint,
): boolean {
  const activityTitle = checkpointDisplayLabel(row).toLowerCase();
  const pointTitle = checkpointDisplayLabel(point).toLowerCase();
  return activityTitle.length > 0 && activityTitle === pointTitle;
}

/**
 * Pick the best scan-point for an Activity row. Prefer id match; fall back to title + time.
 */
export function matchScanPointForActivity(
  row: ScanRow,
  scanPoints: readonly ScanPoint[],
): ScanPoint | null {
  if (row.scanId.length > 0) {
    const exact = scanPoints.find((point) => point.scanPointId === row.scanId);
    if (exact !== undefined) {
      return exact;
    }
  }

  const rowMs = parseIsoMs(row.serverReceivedAt);
  let best: ScanPoint | null = null;
  let bestDelta = Number.POSITIVE_INFINITY;

  for (const point of scanPoints) {
    if (!titlesMatch(row, point)) {
      continue;
    }
    if (row.guardName !== null && point.guardName !== null && row.guardName !== point.guardName) {
      continue;
    }
    if (rowMs === null || point.serverReceivedAt === null) {
      if (best === null) {
        best = point;
      }
      continue;
    }
    const pointMs = parseIsoMs(point.serverReceivedAt);
    if (pointMs === null) {
      continue;
    }
    const delta = Math.abs(pointMs - rowMs);
    if (delta <= MATCH_WINDOW_MS && delta < bestDelta) {
      best = point;
      bestDelta = delta;
    }
  }
  return best;
}

function matchFieldPinForActivity(row: ScanRow, fieldPins: readonly FieldPin[]): FieldPin | null {
  const name = row.checkpointName?.trim();
  if (name === undefined || name.length === 0) {
    return null;
  }
  const lower = name.toLowerCase();
  return fieldPins.find((pin) => pin.name.toLowerCase() === lower) ?? null;
}

/**
 * Resolve where CAD should fly for an Activity row, or null when nothing mappable exists.
 */
export function resolveActivityMapTarget(
  row: ScanRow,
  options: {
    scanPoints: readonly ScanPoint[];
    fieldPins?: readonly FieldPin[];
  },
): ActivityMapTarget | null {
  const title = checkpointDisplayLabel(row);
  const guard = row.guardName ?? "Guard";
  const label = `${title} · ${guard}`;

  const scanPoint = matchScanPointForActivity(row, options.scanPoints);
  if (scanPoint !== null) {
    return {
      lat: scanPoint.lat,
      lng: scanPoint.lng,
      label,
      source: scanPoint.mapCoordSource,
      scanPointId: scanPoint.scanPointId,
      routeAssignmentId: row.routeAssignmentId ?? scanPoint.routeAssignmentId ?? null,
      siteId: scanPoint.siteId ?? null,
      occurredAt: row.serverReceivedAt || scanPoint.serverReceivedAt,
    };
  }

  const pin = matchFieldPinForActivity(row, options.fieldPins ?? []);
  if (pin !== null) {
    return {
      lat: pin.lat,
      lng: pin.lng,
      label,
      source: "checkpoint_pin",
      scanPointId: null,
      routeAssignmentId: row.routeAssignmentId ?? null,
      siteId: null,
      occurredAt: row.serverReceivedAt || null,
    };
  }

  return null;
}

/** Banner line for the CAD activity fly-to highlight. */
export function activityMapFocusBannerText(target: ActivityMapTarget): string {
  const where =
    target.source === "scan_gps"
      ? "scan GPS"
      : target.source === "checkpoint_expected" || target.source === "checkpoint_pin"
        ? "checkpoint pin"
        : "map point";
  return `Activity: ${target.label} · ${where}`;
}
